import { useEffect, useState } from "react";
import { ArrowDown } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import ClosingCTA from "@/components/site/ClosingCTA";
import { Eyebrow, OutLink, PageHero, PillButton, Reveal, Tbc } from "@/components/site/Bits";
import { ACTIVITIES } from "@/lib/content";
import { useSettings } from "@/lib/site";

function Cta({ a }) {
    const s = useSettings();
    if (a.cta.external) {
        return (
            <OutLink href={s[a.cta.external]} event="verde_capital_click" label="what-we-do" testid={`wwd-cta-${a.id}`}
                     className="rounded-full bg-forest text-cream hover:bg-forest-hover px-7 py-4 text-sm font-medium transition-colors">
                {a.cta.label}
            </OutLink>
        );
    }
    return <PillButton to={a.cta.to} testid={`wwd-cta-${a.id}`}>{a.cta.label}</PillButton>;
}

function TaylorNote() {
    const s = useSettings();
    return (
        <div className="mt-8 rounded-xl bg-surface p-6 text-sm leading-relaxed text-muted_text" data-testid="taylor-brokers-note">
            <img src="/taylor-brokers-logo.png" alt="Taylor Brokers" className="h-8 w-auto mb-4" loading="lazy" />
            Residential brokerage is carried out exclusively by our partner <strong className="text-forest">Taylor Brokers</strong>.
            <div className="mt-3">
                {s.taylor_brokers_url
                    ? <OutLink href={s.taylor_brokers_url} event="taylor_brokers_click" label="what-we-do" testid="wwd-taylor-link" className="font-medium text-forest underline">Visit Taylor Brokers</OutLink>
                    : <Tbc>Taylor Brokers website — TBC</Tbc>}
            </div>
        </div>
    );
}

function Activity({ a, i }) {
    const core = i === 0;
    return (
        <section id={a.id} data-testid={`wwd-${a.id}`} className={`scroll-mt-32 py-20 md:py-28 ${core ? "bg-cream" : i % 2 ? "bg-surface" : "bg-cream"}`}>
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <Reveal><Eyebrow>{a.n} / 0{ACTIVITIES.length} {core && "· Core business"}</Eyebrow></Reveal>
                    <Reveal delay={100}><h2 className={`font-serif text-forest leading-[1.04] tracking-tight mt-8 ${core ? "text-5xl sm:text-6xl" : "text-4xl sm:text-5xl"}`}>{a.title}</h2></Reveal>
                    <Reveal delay={150}><p className="mt-6 font-serif italic text-2xl text-moss">{a.tagline}</p></Reveal>
                    {a.id === "financing" && (
                        <Reveal delay={200}><img src="/verde-capital-logo.png" alt="Verde Capital" className="mt-12 h-16 md:h-20 w-auto" loading="lazy" data-testid="wwd-verde-capital-logo" /></Reveal>
                    )}

                    {core && (
                        <Reveal delay={200} className="mt-10 overflow-hidden rounded-2xl aspect-[4/3]">
                            <img src="/land-sintra.jpg" alt="Development land in Sintra" loading="lazy" className="h-full w-full object-cover" />
                        </Reveal>
                    )}
                </div>
                <div className="lg:col-span-7 lg:pt-4">
                    <Reveal className="space-y-8">
                        <div>
                            <p className="mono-label text-muted_text mb-3">What we do</p>
                            <p className="text-lg md:text-xl text-ink leading-relaxed">{a.what}</p>
                        </div>
                        {a.steps && (
                            <div>
                                <p className="mono-label text-muted_text mb-4">{a.stepsLabel || "How it works"}</p>
                                <ol className="grid sm:grid-cols-2 gap-px bg-hairline rounded-xl overflow-hidden">
                                    {a.steps.map((st, j) => (
                                        <li key={st} className={`flex gap-4 p-5 ${i % 2 && !core ? "bg-surface" : "bg-cream"}`}>
                                            <span className="num-mono text-moss text-sm pt-0.5">{String(j + 1).padStart(2, "0")}</span>
                                            <span className="text-sm text-forest leading-relaxed">{st}</span>
                                        </li>
                                    ))}
                                </ol>
                            </div>
                        )}
                        <div className="grid sm:grid-cols-2 gap-8">
                            <div><p className="mono-label text-muted_text mb-3">Who it helps</p><p className="text-base text-muted_text leading-relaxed">{a.who}</p></div>
                            <div><p className="mono-label text-muted_text mb-3">The value</p><p className="text-base text-muted_text leading-relaxed">{a.value}</p></div>
                        </div>
                        {a.partner === "taylor" && <TaylorNote />}
                        <div className="pt-2"><Cta a={a} /></div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

const go = (id) => (e) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.history.replaceState(null, "", `#${id}`);
};

function Overview() {
    return (
        <section data-testid="wwd-overview" className="bg-cream pt-16 md:pt-24 pb-4">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal><p className="mono-label text-moss mb-8">Four ways we work — choose one to read more</p></Reveal>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {ACTIVITIES.map((a, i) => {
                        const Icon = a.icon;
                        return (
                            <Reveal key={a.id} delay={i * 80}>
                                <a href={`#${a.id}`} onClick={go(a.id)} data-testid={`wwd-card-${a.id}`}
                                   className={`group flex h-full flex-col rounded-2xl p-7 min-h-[260px] border transition-[background-color,border-color,transform] duration-300 hover:-translate-y-1 ${i === 0 ? "bg-forest text-cream border-forest" : "bg-surface border-hairline hover:border-forest"}`}>
                                    <div className="flex items-center justify-between mb-8">
                                        <span className={`font-serif text-5xl leading-none ${i === 0 ? "text-sage" : "text-forest"}`}>{a.n}</span>
                                        <Icon className={`h-6 w-6 ${i === 0 ? "text-sage" : "text-moss"}`} strokeWidth={1.5} />
                                    </div>
                                    <h3 className={`font-serif text-2xl leading-tight mb-3 ${i === 0 ? "text-cream" : "text-forest"}`}>{a.title}</h3>
                                    <p className={`text-sm leading-relaxed ${i === 0 ? "text-cream/75" : "text-muted_text"}`}>{a.short}</p>
                                    <span className={`mt-auto pt-6 inline-flex items-center gap-2 mono-label ${i === 0 ? "text-sage" : "text-forest"}`}>
                                        Read more <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
                                    </span>
                                </a>
                            </Reveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

function StickyNav() {
    const [active, setActive] = useState(ACTIVITIES[0].id);
    useEffect(() => {
        const els = ACTIVITIES.map((a) => document.getElementById(a.id)).filter(Boolean);
        const io = new IntersectionObserver((entries) => {
            entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); });
        }, { rootMargin: "-40% 0px -55% 0px" });
        els.forEach((el) => io.observe(el));
        return () => io.disconnect();
    }, []);
    return (
        <nav className="sticky top-[72px] z-30 bg-cream/90 backdrop-blur-xl border-y border-hairline mt-12" aria-label="Activities">
            <div className="max-w-7xl mx-auto px-6 md:px-12 flex gap-2 overflow-x-auto py-3 whitespace-nowrap">
                {ACTIVITIES.map((a) => (
                    <a key={a.id} href={`#${a.id}`} onClick={go(a.id)} data-testid={`wwd-jump-${a.id}`} aria-current={active === a.id ? "true" : undefined}
                       className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${active === a.id ? "bg-forest text-cream" : "text-muted_text hover:text-forest hover:bg-surface"}`}>
                        <span className="num-mono text-xs opacity-70">{a.n}</span>{a.title}
                    </a>
                ))}
            </div>
        </nav>
    );
}

export default function WhatWeDo() {
    return (
        <div data-testid="what-we-do-page" className="bg-cream text-ink min-h-screen">
            <SEO path="/what-we-do" og="what-we-do" title="What We Do — Land development, investment & transactions in Portugal"
                 description="Land development and investment in Greater Lisbon, land acquisition across Portugal, development financing through Verde Capital and Land Intelligence — our AI framework for smarter development decisions." />
            <GroupHeader />
            <PageHero testid="wwd-hero" eyebrow="What we do" image="/v2050-wwd-model.jpg" imageAlt="Architectural massing model of a hillside housing scheme on a topographic base"
                      title={<>We find promising land, secure the approvals, <em className="italic text-sage">and bring projects to market.</em></>}
                      intro="Four activities, one business. Land development is the core; everything else helps good projects happen." />
            <Overview />
            <StickyNav />
            {ACTIVITIES.map((a, i) => <Activity key={a.id} a={a} i={i} />)}
            <ClosingCTA testid="wwd-contact" />
            <GroupFooter />
        </div>
    );
}
